/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/

import clsx from 'clsx';
import React from 'react';
import {UseFormRegisterReturn} from 'react-hook-form';
import {AppFieldWrapper, FieldWrapperPassThroughProps} from './AppFieldWrapper';
import {useState} from "react";
import {apiMediaUrl, apiThemeUploadPath} from '../../utils/AppConfig';

type Option = {
    label: React.ReactNode;
    value: string | number | string[];
    image?: string;
};

type SelectFieldProps = FieldWrapperPassThroughProps & {
    options: Option[];
    className?: string;
    defaultValue?: string;
    registration: Partial<UseFormRegisterReturn>;
    hasImage?: boolean;
    label: string;
    image?: string;
    tooltip?: any;
};

export const AppSelect = (props: SelectFieldProps) => {
    const {label, options, error, className, defaultValue, registration, hasImage, image} = props;
    const [currentImage, setCurrentImage] = useState(image);
    const uploadPath = apiMediaUrl + apiThemeUploadPath;

    const switchThemeImage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!hasImage) return;

        const selectedValue = event.target.value;
        const selectedOption = options.find(option => option.value === selectedValue);
        if (selectedOption) {
            let imageLabel = selectedOption.label?.toString().toLowerCase();
            if (imageLabel && !imageLabel.endsWith('.webp')) {
                imageLabel += '.webp';
            }
            setCurrentImage(imageLabel);
        }
    }

    return (
        <AppFieldWrapper error={error} className={hasImage ? "w-full" : ""}>
            <div className="relative">
                <div className={`${hasImage && 'absolute top-2 left-4 text-xs text-black rounded-sm bg-white p-2'}`}>{label}</div>
                <select
                    className={clsx(
                        'mt-2 cursor-pointer flex text-md text-mono-1000 -full border border-mono-1000  focus:outline-hidden  rounded-sm bg-white/30 p-3 outline-hidden',
                        className
                    )}
                    style={hasImage ? {
                        backgroundImage: `url("${uploadPath}${currentImage}")`,
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                        height: '125px'
                    } : {
                        height: 'auto'
                    }}
                    defaultValue={defaultValue}
                    {...registration}
                    onChange={switchThemeImage}
                >
                    {options && options.map(({label, value}) => (
                        <option key={label?.toString()} value={value} className={'text-mono-600'}>
                            {label}
                        </option>
                    ))}
                </select>
            </div>
        </AppFieldWrapper>
    );
};
